import type { Guide } from "./types.js";
import { Lead, H2, Ul, Li, AppLink, C, Callout } from "./primitives.js";

export const meta = {
  id: "spend-dashboard",
  title: "Spend dashboard — per-call cost log",
  category: "Reference",
  lastUpdated: "2026-06-08",
  description: "Every billable API call writes a row to cost_ledger. The Spend page surfaces totals, per-provider/per-operation breakdowns, daily timeline, and a 200-row recent ledger.",
  relatedGuides: ["cost-optimization", "error-log", "troubleshooting"],
  readTimeMin: 5,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        The <AppLink to="spend">Spend</AppLink> page reads from a SQLite ledger (<C>cost_ledger</C>, migration 020) that captures every paid API call across the pipeline. Anthropic, Bedrock, ElevenLabs, Replicate (Flux + Wan), OpenAI image-gen, SerpAPI, Firecrawl, DataForSEO. Totals roll up by provider, operation, and time window. Polls every 60s.
      </Lead>

      <H2>What gets captured</H2>
      <Ul>
        <Li><strong>LLM calls</strong> — brief generation, article production (per section + meta), storyboard, narration script, cluster classifier + expander, topic extractor. Tokens (input / output / cache-read / cache-write) all logged.</Li>
        <Li><strong>Replicate</strong> — every Flux 2 Pro / Schnell image + every Wan 2.2 / LTX-2.3 video. Per-unit pricing applied via the central calculator.</Li>
        <Li><strong>ElevenLabs</strong> — every TTS call, with character count + per-1K pricing (Starter $0.18, Creator $0.16 — configurable per call).</Li>
        <Li><strong>SerpAPI</strong> — one row per enrichment (4 sub-calls rolled up).</Li>
        <Li><strong>Firecrawl</strong> — one row per <C>scrape-competitors</C> run with attempted/successful counts in <C>notes</C>.</Li>
        <Li>Each row carries <C>operation</C> (dotted namespace, same as the error log), optional <C>keyword_id</C>, <C>model</C>, raw token / char / unit counts, and <C>estimated_cost_usd</C>.</Li>
      </Ul>

      <Callout kind="tip">
        Pricing changes? Update <C>keywords/src/cost/calculator.ts</C>. The dashboard recomputes against the new table immediately for future calls; historical rows keep the rate they were logged at.
      </Callout>

      <H2>Page layout</H2>
      <Ul>
        <Li><strong>Window picker</strong> — Month-to-date (default), 7d, 30d, all-time. Drives every query on the page.</Li>
        <Li><strong>KPI tiles</strong> — total spend (sum of <C>estimated_cost_usd</C>), top provider, top operation.</Li>
        <Li><strong>By provider</strong> — share-of-spend bar chart in a table.</Li>
        <Li><strong>By operation · top 20</strong> — same shape, scoped to operations.</Li>
        <Li><strong>Daily timeline</strong> — sparkline-style bars by day.</Li>
        <Li><strong>Recent calls</strong> — last 200 rows, with provider pill, operation, keyword link, model, tokens/chars/units, cost.</Li>
      </Ul>

      <H2>What it's for</H2>
      <Ul>
        <Li><strong>Bedrock vs Anthropic comparison</strong>: once the provider toggle flips, daily timeline will show whether caching held. If <C>bedrock</C> overtakes <C>anthropic</C> in the per-provider breakdown without total spend dropping, caching broke.</Li>
        <Li><strong>Per-article cost</strong>: filter Recent by a keyword id (URL: <C>#/pipeline?keyword=...</C> already links from the table). The per-keyword endpoint <C>GET /api/cost/keyword/:id</C> rolls up across provider + operation + model.</Li>
        <Li><strong>Catching a runaway</strong>: a sudden spike in <C>image-gen.in_content</C> over a week means a stuck loop. The daily timeline + per-operation tables are the first place to look.</Li>
        <Li><strong>Spend gating before launch</strong>: see total monthly spend in one tile so you can run a $5 budget check before scaling to 5 articles/week.</Li>
      </Ul>

      <H2>Known limitations</H2>
      <Ul>
        <Li>Cost numbers are <strong>estimates</strong>, not vendor-billed amounts. Real invoices may diverge on cached calls, tier breakpoints, or rounding. Verify quarterly against the vendor dashboards.</Li>
        <Li>The Wan 2.2 calculator uses a flat per-video estimate (~$0.10 / 8s clip). Actual Wan billing is per-second; if you change <C>durationSec</C> meaningfully, tune the calculator.</Li>
        <Li>Distribution calls (Telegram / Brevo / Meta / X / TikTok / YouTube / WhatsApp) currently cost $0 to the API — no ledger rows are written for them. If we ever start paying for distribution, add the providers to the calculator + log at the orchestrator level.</Li>
        <Li>Cache pricing for Anthropic is hardcoded at 10% of input (cache-read) and 125% of input (cache-write). Update if vendor pricing shifts.</Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
